import { CountryCode } from '@doordash/lib-i18n'
import { get, has, isEmpty } from 'lodash'
import { routerReducer as router } from 'react-router-redux'
import { combineReducers } from 'redux'
import { Application } from 'src/utilities/application/application'
import * as actions from '../actions'
import { GetFirstDashCampaignsSuccessPayload } from '../api/dasherPromotions'
import { WorkflowTemplate } from '../components/organisms/workflow-template/types'
import { UNIQUE_LINK_KEY } from '../constants'
import {
  ADDRESS_COLLECTION_INSTANT_DASH,
  DX_R2A_LOCAL_STORAGE_AUTO_RESUME,
  INSTANT_DASHING_V2,
  IS_AT_CAPACITY_V2,
} from '../experimentConstants'
import {
  AlreadyStartedSigningUpModalProps,
  ApplicationStatus,
  DeliveryIssue,
  GetSales,
  IDVStatusResponse,
  OnBoardingWorkflow,
  Referral,
  StripeCredentials,
  VehicleDashStatusForVehicle,
} from '../models/types'
import routes from '../routes'
import * as geCampaignType from '../typeModules/guaranteedEarningsCampaign'
import * as geModalType from '../typeModules/guaranteedEarningsRedirectModal'
import SimpleStore from '../utilities/SimpleStore'
import { IDVStatus } from '../utilities/steps'
import {
  backgroundCheckExternalLink,
  BackgroundCheckExternalLinkState,
} from './backgroundCheckExternalLink'
import firstDashCampaigns from './dasherPromotions'
import { dxTrainingCourse } from './dx-training'
import {
  getInitialState as getInitialPrivacyState,
  PrivacyOptions,
} from './privacy'
import isRedirectToProgressionPage from './redirectToProgressionPage'

export type RootState = {
  router: any
  application: Application
  dasher: {
    defaultStartingPoint: string
    rewardsTier: string
  }
  startingPoint: {}
  dasherId: string
  country: CountryCode
  vehicleTypes: any
  referrer: Referral
  dasherApplyPageContent: any
  guaranteedEarningsCampaign: geCampaignType.GECampaign
  guaranteedEarningsCode: string
  guaranteedEarningsRedirectModal: any
  dasherApplySubmittingApplication: any
  consumerEmail: string
  isAtCapacity: boolean
  isLoggedIn: boolean
  isLoadingApplication: boolean
  isSubmittingProfile: boolean
  hasAbn: boolean
  experiments: any
  supplyAcquisitionMethod: string
  w9Link: any
  mailingAddress: any
  referralCampaignId: number | null
  signupCountryShortname: string | null
  stripeCredentials: StripeCredentials
  paymentAccount: {
    id: string
    stripeAccountId: string
    stripeAccountType: string
  }
  applicationStatus: object
  stripeFileUpload: object
  alreadyStartedSigningUpModal: object
  applicantUniqueLink: string | null
  deliveryIssuesList: {
    entities: DeliveryIssue[]
    isLoading: boolean
    errors: object
  }
  deliveryIssueDetails: DeliveryIssue
  fastPaySetup: any
  getSales: GetSales
  apiStatusCode: number | null
  idvStatus: IDVStatusResponse
  onboardingStep: string
  idvSessionToken: string
  startingPointStatus: {
    id: number
    vehicleDashStatus: VehicleDashStatusForVehicle[]
  }[]
  onBoardingWorkflow: OnBoardingWorkflow
  firstDashCampaigns: GetFirstDashCampaignsSuccessPayload[]
  privacyOptions: PrivacyOptions
  backgroundCheckExternalLink: BackgroundCheckExternalLinkState
}

export function getInitialState(data): RootState {
  return {
    // Reducers for the app
    router: routes.dasherlanding.buildPath({}),
    application: data.application,
    dasher: {
      defaultStartingPoint: data.default_starting_point,
      rewardsTier: data.rewards_tier,
    },
    startingPointStatus: [],
    startingPoint: {},
    dasherId: get(data, 'application.dasherId', null),
    country: data.country,
    vehicleTypes: data.vehicle_types,
    referrer: {
      id: data.referrer_id,
      name: data.referrer_name,
      referreeBonus: data.referree_bonus,
      referralExpiration: data.referral_expiration,
      referralExpirationInDays: data.referral_expiration_in_days,
      targetNumDeliveries: data.referral_target_num_deliveries,
    },
    dasherApplyPageContent: {
      submarketLinks: data.submarket_links,
      copy: data.copy,
      submarket: data.submarket,
    },
    guaranteedEarningsCampaign:
      data.guaranteed_earnings_campaign &&
      data.guaranteed_earnings_campaign.is_expired
        ? null
        : data.guaranteed_earnings_campaign,
    guaranteedEarningsCode:
      data.guaranteed_earnings_campaign &&
      data.guaranteed_earnings_campaign.is_expired
        ? null
        : data.guaranteed_earnings_code,
    guaranteedEarningsRedirectModal:
      data.guaranteed_earnings_campaign &&
      data.guaranteed_earnings_campaign.is_expired
        ? { type: geModalType.GEType.EXPIRED }
        : null,
    dasherApplySubmittingApplication: null,
    consumerEmail: null,
    isAtCapacity: get(data, 'is_at_capacity', false),
    isLoggedIn: false,
    isLoadingApplication: false,
    isSubmittingProfile: false,
    hasAbn: false,
    experiments: data.experiments || null,
    supplyAcquisitionMethod: '',
    w9Link: {
      isSubmitting: false,
    },
    mailingAddress: {
      isSubmitting: false,
      isSubmitted: false,
      error: null,
      data: {
        street: '',
        streetLine2: '',
        city: '',
        state: '',
        postalCode: '',
      },
    },
    stripeCredentials: {
      publicKey: '',
      countryShortname: '',
      currencyCode: '',
    },
    paymentAccount: {
      id: '',
      stripeAccountId: null,
      stripeAccountType: null,
    },
    referralCampaignId: get(data, 'referral_campaign_id', null),
    signupCountryShortname:
      get(data, 'signup_country_shortname') ||
      get(data, 'country_shortname') ||
      get(data, 'country', null),
    applicationStatus: {
      data: {
        hasLoaded: false,
        bgcheckStatus: 'WAITING',
        thinFileStatus: 'CLEAR',
        shippingAddress: '',
        shippingArrivalDate: '',
        shippingStatus: '',
        shippingUrl: '',
        step: '1',
        onboardingProgress: {
          backgroundCheckComplete: false,
          hasCompletedFirstDelivery: false,
          hasCompletedSafetyCourse: true,
          hasScheduledDash: false,
          paymentAccountSetupComplete: false,
          redCardActivationComplete: false,
          abnComplete: false,
        },
        verificationStatus: {
          documentsRequired: [],
          needsVerification: false,
          isDismissible: true,
        },
      },
    },
    stripeFileUpload: {},
    alreadyStartedSigningUpModal: {
      isShowing: false,
      email: null,
      phoneNumber: null,
      type: null,
    },
    applicantUniqueLink:
      data.experiments && data.experiments[DX_R2A_LOCAL_STORAGE_AUTO_RESUME]
        ? SimpleStore.get(UNIQUE_LINK_KEY)
        : null,
    deliveryIssuesList: deliveryIssuesListInitialState,
    deliveryIssueDetails: {} as DeliveryIssue,
    fastPaySetup: null,
    getSales: {
      isSubmitting: false,
    },
    apiStatusCode: null,
    idvStatus: {
      value: IDVStatus.NOT_CREATED,
    },
    onboardingStep: null,
    idvSessionToken: null,
    onBoardingWorkflow: {
      currentStep: '',
      currentStepStatus: 'STEP_STATUS_UNSPECIFIED',
      template: {
        pages: [],
      } as WorkflowTemplate,
      isLoading: false,
      error: '',
      stepsComplete: false,
    },
    firstDashCampaigns: null,
    privacyOptions: getInitialPrivacyState(data),
    backgroundCheckExternalLink: {},
  }
}

// ####################################
// Helpers
// ####################################

const parseOrientation = orientation => {
  if (!isEmpty(orientation)) {
    orientation.startTime = new Date(orientation.startTime)
    orientation.endTime = new Date(orientation.endTime)
    return orientation
  }

  return null
}

// ####################################
// Reducers
// ####################################

const orientations = (state = [], action) => {
  if (action.type === actions.LOAD_ORIENTATIONS.SUCCESS) {
    if (action.payload) {
      return action.payload.map(parseOrientation)
    }
  }
  return state
}

const orientationStartingPoint = (state = null, action) => {
  if (action.type === actions.PICK_ORIENTATION_STARTING_POINT) {
    return action.startingPoint
  }
  return state
}

const scheduledOrientation = (state = null, action) => {
  if (action.type === actions.CLEAR_ORIENTATION.SUCCESS) {
    return null
  }

  if (action.type === actions.BOOK_ORIENTATION.SUCCESS) {
    return parseOrientation(action.payload)
  }

  if (action.type === actions.LOAD_DASHER_APPLICATION.SUCCESS) {
    return parseOrientation(action.payload.dasherOrientation)
  }
  return state
}

const isAtCapacity = (state = false) => {
  return state
}

const isLoggedIn = (state = false, action) => {
  if (
    action.type === actions.LOAD_CONSUMER_APPLICATION.SUCCESS ||
    action.type === actions.LOGIN_DASHER_APPLICATION.SUCCESS ||
    action.type === actions.LOAD_USER.SUCCESS
  ) {
    return true
  }
  return state
}

const consumerEmail = (state = null, action) => {
  if (
    action.type === actions.LOAD_CONSUMER_APPLICATION.SUCCESS ||
    action.type === actions.LOGIN_DASHER_APPLICATION.SUCCESS ||
    action.type === actions.LOAD_USER.SUCCESS
  ) {
    return action.payload.email
  }
  return state
}

const isLoadingApplication = (state = false, action) => {
  switch (action.type) {
    case actions.LOAD_DASHER_APPLICATION_FOR_BGC.IN_PROGRESS:
      return !action.payload.isReload
    case actions.LOAD_DASHER_APPLICATION.IN_PROGRESS:
      return true
    case actions.LOAD_DASHER_APPLICATION.SUCCESS:
    case actions.LOAD_DASHER_APPLICATION.FAIL:
      return false
  }
  return state
}

const isSubmittingProfile = (state = false, action) => {
  switch (action.type) {
    case actions.SUBMIT_PERSONAL_INFO.IN_PROGRESS:
      return true
    case actions.SUBMIT_PERSONAL_INFO.SUCCESS:
    case actions.SUBMIT_PERSONAL_INFO.FAIL:
      return false
  }
  return state
}

const application = (state = null, action) => {
  switch (action.type) {
    case actions.LOAD_DASHER_APPLICATION.SUCCESS:
      return {
        ...action.payload,
      }
    case actions.LOAD_DASHER_APPLICATION_FOR_BGC.SUCCESS:
      return {
        ...action.payload,
        isReload: false,
      }
    case actions.CREATE_CONSUMER_ACCOUNT.SUCCESS:
      return { ...state, hasConsumerAccount: true }
    case actions.LOAD_DASHER_APPLICATION.FAIL:
      return action.payload
    case actions.LOAD_DASHER_APPLICATION_FOR_BGC.FAIL:
      return {
        ...action.payload,
        isReload: false,
      }
    case actions.SUBMIT_PERSONAL_INFO.SUCCESS:
      return { ...state, completedProfileStep: true }
    case actions.SUBMIT_PERSONAL_INFO_SUCCESS_NEED_LICENSE:
      return { ...state, needsLicense: true }
    case actions.UPDATE_DASHER_APPLICANT.SUCCESS:
      return { ...state, ...action.payload }
    case actions.SUBMIT_BACKGROUND_CHECK.IN_PROGRESS:
      return {
        ...state,
        submittingBackgroundCheck: true,
      }
    case actions.SUBMIT_BACKGROUND_CHECK.SUCCESS:
      return {
        ...state,
        needsBgCheckInfo: false,
        submittingBackgroundCheck: false,
        backgroundCheckCompletionUrl: action.payload.checkrCompletionUrl,
      }
    default:
      return state
  }
}

const dasherApplyPageContent = (state = {}) => state

const dasherApplySubmittingApplication = (state = false, action) => {
  switch (action.type) {
    case actions.SUBMIT_DASHER_APPLICATION.IN_PROGRESS:
      return true
    case actions.SUBMIT_DASHER_APPLICATION.FAIL:
    case actions.GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_AVAILABLE().type:
    case actions.GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_NOT_AVAILABLE().type:
      return false
    default:
      return state
  }
}

const dasherId = (state = null, action) => {
  if (action.type === actions.CREATE_DASHER_ACCOUNT.SUCCESS) {
    return action.payload.id || state
  }
  if (action.type === actions.LOAD_DASHER_APPLICATION.SUCCESS) {
    return get(action, 'payload.dasherId', null)
  }
  if (action.type === actions.LOAD_DASHER_ID.SUCCESS) {
    return action.payload.id
  }
  if (action.type === actions.SET_DASHER_ID.SUCCESS) {
    return action.payload.id
  }
  return state
}

const country = (state = 'US') => state

const referrer = (state = { id: null, name: '' }) => state

const guaranteedEarningsCampaign = (state = null, action) => {
  switch (action.type) {
    case actions.GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_AVAILABLE().type:
      return action.campaign
    default:
      return state
  }
}

const guaranteedEarningsCode = (state = null, action) => {
  switch (action.type) {
    case actions.GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_AVAILABLE().type:
      return action.campaign.unique_hash
    default:
      return state
  }
}

const guaranteedEarningsRedirectModal = (state = null, action) => {
  switch (action.type) {
    case actions.GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_AVAILABLE().type:
      return {
        type: geModalType.GEType.ELIGIBLE_FOR_ALTERNATIVE_CAMPAIGN,
        campaign: action.campaign,
        applicationData: action.applicationData,
      }
    case actions.GUARANTEED_EARNINGS_WRONG_ZIP.CAMPAIGN_NOT_AVAILABLE().type:
      return {
        type: geModalType.GEType.NO_OFFER_AVAILABLE,
        applicationData: action.applicationData,
      }
    case actions.dismissGuaranteedEarningsRedirectModal().type:
      return null
    default:
      return state
  }
}

const defaultVehicleType = {
  1: {
    needs_license_info: true,
    name: 'car',
  },
  2: {
    needs_license_info: false,
    name: 'bicycle',
  },
}

const vehicleTypes = (state = defaultVehicleType) => {
  return state
}

export const experiments = (state = null, action) => {
  switch (action.type) {
    case actions.LOAD_DASHER_APPLICATION.SUCCESS:
      return {
        ...state,
        [INSTANT_DASHING_V2]: action.payload.isInstantDashingV2,
        [IS_AT_CAPACITY_V2]: action.payload.isAtCapacityV2,
        [ADDRESS_COLLECTION_INSTANT_DASH]:
          get(state, 'address_collection_after_dash', false) &&
          get(state, 'instant_dashing_v2', false),
      }
    case actions.LOAD_WEBVIEW_EXPERIENCE.IN_PROGRESS:
      return {
        ...state,
        isLoading: true,
      }
    case actions.LOAD_WEBVIEW_EXPERIENCE.SUCCESS:
      return {
        ...state,
        ...action.payload.data,
        isLoading: false,
      }
    case actions.LOAD_WEBVIEW_EXPERIMENTS.SUCCESS:
      return Object.keys(action.payload.data).reduce(
        (result, key) => ({
          ...result,
          [key]: action.payload.data[key] === 'treatment',
        }),
        {}
      )
    case actions.GET_EXPERIMENT.SUCCESS:
      return {
        ...state,
        [action.payload.data.name]: action.payload.data.value === 'treatment',
      }
    default:
      return state
  }
}

const noOpReducer = (state = null) => {
  return state
}

const submittingOrientation = (state = null) => {
  return state
}

const clearingOrientation = (state = null) => {
  return state
}

const errorMessage = (state = null, action) => {
  const getErrorMessage = errors => {
    const response = get(errors, 'response', null)
    let err = null

    if (response) {
      const genericError = get(response, 'statusText', null)

      let responseMessage = null

      // If the response is an object, show that instead of a
      // generic error like 500 internal server error or something
      if (response.data && typeof response.data === 'object') {
        responseMessage = response.data[Object.keys(response.data)[0]]
      }

      err = {
        message: responseMessage || errors.message,
        response: responseMessage ? '' : `(${genericError})`,
      }
    }

    let message = err || 'Oh no! An error has occurred'
    if (err !== null) {
      const fieldErrors = Object.keys(err)

      if (fieldErrors.length && typeof err !== 'string') {
        message = fieldErrors
          .map(field => {
            return err[field]
          })
          .join('\n')
      }
    }

    return message
  }

  if (action.type === actions.SHOW_ERROR) {
    const message = action.message || getErrorMessage(action.error)
    return message
  }

  if (action.type === actions.CLEAR_ERRORS) {
    return null
  }
  return state
}

const supplyAcquisitionMethod = (state = '', action) => {
  switch (action.type) {
    case actions.SET_SUPPLY_ACQUISITION_METHOD:
      return action.method
    default:
      return state
  }
}

const w9Link = (
  state = {
    isSubmitting: false,
  },
  action
) => {
  switch (action.type) {
    case actions.GO_TO_W9.IN_PROGRESS:
      return { ...state, isSubmitting: true }
    case actions.GO_TO_W9.SUCCESS:
    case actions.GO_TO_W9.FAIL:
      return { ...state, isSubmitting: false }
    default:
      return state
  }
}

const applicationStatus = (
  state: { isLoading: boolean; data: ApplicationStatus } = {
    isLoading: true,
    data: {} as ApplicationStatus,
  },
  action
) => {
  switch (action.type) {
    case actions.GET_APPLICATION_STATUS.IN_PROGRESS:
      return { ...state, isLoading: true, isReload: action.payload.isReload }
    case actions.GET_APPLICATION_STATUS.SUCCESS:
      return {
        ...state,
        data: { ...action.payload, hasLoaded: true },
        isLoading: false,
        isReload: false,
      }
    case actions.GET_APPLICATION_STATUS.FAIL:
      return { ...state, isLoading: false, isReload: false }
    default:
      return state
  }
}

const mailingAddress = (
  state = {
    isSubmitting: false,
    isSubmitted: false,
    error: null,
    street: '',
    streetLine2: '',
    city: '',
    state: '',
    postalCode: '',
  },
  action
) => {
  switch (action.type) {
    case actions.LOAD_DASHER_APPLICATION.SUCCESS:
      if (has(action, 'payload.mailingAddress')) {
        if (!action.payload.mailingAddress) {
          return state
        }

        return {
          ...state,
          isSubmitted: true,
          ...action.payload.mailingAddress,
        }
      }
      return state
    case actions.UPDATE_MAILING_ADDRESS:
      return { ...state, ...action.payload }
    case actions.PATCH_PAYMENT_ACCOUNT.IN_PROGRESS:
      return { ...state, isSubmitting: true, error: null }
    case actions.PATCH_PAYMENT_ACCOUNT.FAIL:
      return { ...state, isSubmitting: false }
    case actions.SUBMIT_MAILING_ADDRESS.IN_PROGRESS:
      return { ...state, isSubmitting: true, error: null }
    case actions.SUBMIT_MAILING_ADDRESS.SUCCESS:
      return { ...state, isSubmitting: false, isSubmitted: true }
    case actions.SUBMIT_MAILING_ADDRESS.FAIL:
      return { ...state, isSubmitting: false, error: action.payload }
    default:
      return state
  }
}

const redCardActivation = (state = { isSubmitting: false }, action) => {
  switch (action.type) {
    case actions.ACTIVATE_REDCARD.IN_PROGRESS:
      return { ...state, isSubmitting: true }
    case actions.ACTIVATE_REDCARD.SUCCESS:
    case actions.ACTIVATE_REDCARD.FAIL:
      return { ...state, isSubmitting: false }
    default:
      return state
  }
}

const stripeCredentials = (state = {}, action) => {
  switch (action.type) {
    case actions.GET_STRIPE_CREDENTIALS.SUCCESS:
      return { ...state, ...action.payload }
    case actions.GET_STRIPE_CREDENTIALS.FAIL:
      return { ...state }
    default:
      return state
  }
}

const createPaymentAccount = (state = {}, action) => {
  switch (action.type) {
    case actions.CREATE_PAYMENT_ACCOUNT.IN_PROGRESS:
    case actions.CREATE_PAYMENT_ACCOUNT.SUCCESS:
    case actions.CREATE_PAYMENT_ACCOUNT.FAIL:
    default:
      return state
  }
}

const paymentAccount = (state = {}, action) => {
  switch (action.type) {
    case actions.GET_PAYMENT_ACCOUNT.SUCCESS:
    case actions.CREATE_PAYMENT_ACCOUNT.SUCCESS:
      return action.payload
    case actions.GET_PAYMENT_ACCOUNT.FAIL:
    default:
      return state
  }
}
const updateBankAccount = (state = {}, action) => {
  switch (action.type) {
    case actions.UPDATE_BANK_ACCOUNT.IN_PROGRESS:
    case actions.UPDATE_BANK_ACCOUNT.SUCCESS:
    case actions.UPDATE_BANK_ACCOUNT.FAIL:
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const hasAbn = (state = false, action) => {
  switch (action.type) {
    case actions.GET_HAS_ABN.SUCCESS:
      return action.payload.hasAbn
    case actions.GET_HAS_ABN.FAIL:
      return false
  }
  return state
}

const referralCampaignId = (state = null) => {
  return state
}

const signupCountryShortname = (state = null) => {
  return state
}

const stripeFileUpload = (state = {}, action) => {
  switch (action.type) {
    case actions.UPLOAD_PHOTO.SUCCESS:
      return { ...state, ...action.payload }
    case actions.UPLOAD_PHOTO.FAIL:
      return { ...state }
    default:
      return state
  }
}

export const alreadyStartedSigningUpModal = (
  state: AlreadyStartedSigningUpModalProps = {
    isShowing: false,
    email: null,
    phoneNumber: null,
    type: null,
  },
  action
) => {
  switch (action.type) {
    case actions.TOGGLE_ALREADY_STARTED_SIGNING_UP_MODAL:
      return {
        ...action.payload,
        isShowing: !state.isShowing,
      }
    default:
      return state
  }
}

export const dasher = (state = null, action) => {
  switch (action.type) {
    case actions.LOAD_DASHER_SERIALIZER.IN_PROGRESS:
      return { ...state, isLoading: true }
    case actions.LOAD_DASHER_SERIALIZER.FAIL:
      return null
    case actions.LOAD_DASHER_SERIALIZER.SUCCESS:
      return { ...state, isLoading: false, ...action.payload }
    default:
      return state
  }
}
export const startingPointStatus = (state = null, action) => {
  switch (action.type) {
    case actions.LOAD_STARTING_POINT_STATUS.IN_PROGRESS:
    case actions.LOAD_STARTING_POINT_STATUS.FAIL:
      return null
    case actions.LOAD_STARTING_POINT_STATUS.SUCCESS:
      return action.payload
    default:
      return state
  }
}
export const startingPoint = (state = null, action) => {
  switch (action.type) {
    case actions.LOAD_STARTING_POINT_BY_POINT.IN_PROGRESS:
      return { ...state, isLoading: true }
    case actions.LOAD_STARTING_POINT_BY_POINT.FAIL:
      return { ...state, isLoading: false }
    case actions.LOAD_STARTING_POINT_BY_POINT.SUCCESS:
      return { ...state, isLoading: false, ...action.payload }
    default:
      return state
  }
}
export const applicantUniqueLink = (state = null, action) => {
  switch (action.type) {
    case actions.LOAD_DASHER.IN_PROGRESS:
    case actions.LOAD_DASHER.FAIL:
      return null
    case actions.LOAD_DASHER.SUCCESS:
      return action.payload.applicant_unique_link
    default:
      return state
  }
}

const deliveryIssuesListInitialState = {
  entities: [],
  isLoading: true,
  errors: {},
}

export const deliveryIssuesList = (
  state = deliveryIssuesListInitialState,
  action
) => {
  switch (action.type) {
    case actions.GET_DELIVERY_ISSUES_LIST.IN_PROGRESS:
      return {
        entities: [],
        isLoading: true,
        errors: {},
      }
    case actions.GET_DELIVERY_ISSUES_LIST.SUCCESS:
      return {
        entities: action.payload,
        isLoading: false,
        errors: {},
      }
    case actions.GET_DELIVERY_ISSUES_LIST.FAIL:
      return {
        entities: [...state.entities],
        isLoading: false,
        errors: action.payload,
      }
    default:
      return state
  }
}

export const deliveryIssueDetails = (state = {}, action) => {
  switch (action.type) {
    case actions.GET_DELIVERY_ISSUE_DETAILS.SUCCESS:
    case actions.MARK_DELIVERY_ISSUE_AS_READ.SUCCESS:
      return action.payload
    default:
      return state
  }
}

export const deliveryIssueDispute = (state = {}, action) => {
  switch (action.type) {
    case actions.SUBMIT_DELIVERY_ISSUE_DISPUTE.IN_PROGRESS:
      return { ...state, isSubmitting: true, isSubmitted: false, error: null }
    case actions.SUBMIT_DELIVERY_ISSUE_DISPUTE.SUCCESS:
      return { ...state, isSubmitting: false, isSubmitted: true, error: null }
    case actions.SUBMIT_DELIVERY_ISSUE_DISPUTE.FAIL:
      return {
        ...state,
        isSubmitting: false,
        isSubmitted: false,
        error: action.payload,
      }
    case actions.CLEAR_DELIVERY_ISSUE_DISPUTE:
      return { isSubmitting: false, isSubmitted: false, error: null }
    default:
      return state
  }
}

export const fastPaySetup = (state = {}, action) => {
  switch (action.type) {
    case actions.SUBMIT_FAST_PAY_SETUP.IN_PROGRESS:
    case actions.SUBMIT_FAST_PAY_SETUP.SUCCESS:
      return { ...state }
    case actions.SUBMIT_FAST_PAY_SETUP.FAIL:
      return {
        ...state,
        error: action.payload,
      }
    default:
      return state
  }
}

const getSales = (state = { isSubmitting: false }, action) => {
  switch (action.type) {
    case actions.GET_SALES.IN_PROGRESS:
      return { ...state, isSubmitting: true }
    case actions.GET_SALES.SUCCESS:
      return { ...state, isSubmitting: false, ...action.payload }
    case actions.GET_SALES.FAIL:
      return { ...state, isSubmitting: false }
    default:
      return state
  }
}

const apiStatusCode = (state = {}, action) => {
  switch (action.type) {
    case actions.SET_API_STATUS_CODE:
      return action.payload
    default:
      return state
  }
}

const onboardingStep = (state = null, action) => {
  switch (action.type) {
    case actions.SET_ONBOARDING_STEP:
      return action.payload
    default:
      return state
  }
}

const idvStatus = (state = {}, action) => {
  switch (action.type) {
    case actions.UPDATE_IDV_STATUS.IN_PROGRESS:
      return { ...state, isSubmitting: true, isSubmitted: false, value: null }
    case actions.UPDATE_IDV_STATUS.SUCCESS:
      return {
        ...state,
        isSubmitting: false,
        isSubmitted: true,
        value: action.payload,
      }
    case actions.UPDATE_IDV_STATUS.FAIL:
      return { ...state, error: action.payload }
    default:
      return state
  }
}

const idvSessionToken = (state = null, action) => {
  switch (action.type) {
    case actions.GET_IDV_SESSION_TOKEN.IN_PROGRESS:
      return { ...state }
    case actions.GET_IDV_SESSION_TOKEN.SUCCESS:
      return {
        ...state,
        ...action.payload,
      }
    case actions.GET_IDV_SESSION_TOKEN.FAIL:
      return { ...state, error: action.payload }
    default:
      return state
  }
}

const onBoardingWorkflow = (state = {}, action) => {
  switch (action.type) {
    case actions.CREATE_ONBOARDING_WORKFLOW.IN_PROGRESS:
      return {
        ...state,
        isLoading: true,
      }
    case actions.CREATE_ONBOARDING_WORKFLOW.SUCCESS:
      return {
        ...state,
        ...action.payload,
        isLoading: false,
      }
    case actions.CREATE_ONBOARDING_WORKFLOW.FAIL:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }
    case actions.GET_ONBOARDING_WORKFLOW.IN_PROGRESS:
      return {
        ...state,
        isLoading: true,
      }
    case actions.GET_ONBOARDING_WORKFLOW.SUCCESS:
      return {
        ...state,
        ...action.payload,
        isLoading: false,
      }
    case actions.GET_ONBOARDING_WORKFLOW.FAIL:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }
    case actions.UPDATE_ONBOARDING_WORKFLOW.IN_PROGRESS:
      return {
        ...state,
        isLoading: true,
      }
    case actions.UPDATE_ONBOARDING_WORKFLOW.SUCCESS:
      return {
        ...state,
        ...action.payload,
        isLoading: false,
      }
    case actions.UPDATE_ONBOARDING_WORKFLOW.FAIL:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }
    default:
      return state
  }
}

export const reducer = combineReducers<RootState>({
  router,
  consumerEmail,
  isAtCapacity,
  isLoggedIn,
  isLoadingApplication,
  isSubmittingProfile,
  application,
  dasherId,
  dasherApplyPageContent,
  dasherApplySubmittingApplication,
  country,
  referrer,
  guaranteedEarningsCampaign,
  guaranteedEarningsCode,
  guaranteedEarningsRedirectModal,
  vehicleTypes,
  errorMessage,
  experiments,
  supplyAcquisitionMethod,
  w9Link,
  mailingAddress,
  submittingOrientation,
  clearingOrientation,
  orientations,
  orientationStartingPoint,
  scheduledOrientation,
  applicationStatus,
  redCardActivation,
  referralCampaignId,
  signupCountryShortname,
  stripeCredentials,
  paymentAccount,
  createPaymentAccount,
  updateBankAccount,
  hasAbn,
  stripeFileUpload,
  alreadyStartedSigningUpModal,
  applicantUniqueLink,
  deliveryIssuesList,
  deliveryIssueDetails,
  deliveryIssueDispute,
  fastPaySetup,
  getSales,
  apiStatusCode,
  idvStatus,
  onboardingStep,
  idvSessionToken,
  startingPointStatus,
  startingPoint,
  dasher,
  onBoardingWorkflow,
  firstDashCampaigns,
  isRedirectToProgressionPage,
  privacyOptions: noOpReducer,
  backgroundCheckExternalLink,
  dxTrainingCourse,
})
